import { Checkbox } from "@mui/material";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { ApiService, CoveragePhaseService } from "../../../../../api";
import { translate } from "../../../../providers";
import { isNonEmptyArray, isNonEmptyObject } from "../../../../utils";
import { icon, IconComponent } from "../../../icon-component";
import { CircularLoader } from "../../../loaders";
import styles from "./ProjectPhaseFilter.module.css";

const ProjectPhaseFilter = ({ onChange, request, search, value, readOnly }) => {
	const projectId = useSelector(({ context }) => context.project?.id);
	const [isLoading, setIsLoading] = useState(false);
	const [phases, setPhases] = useState([]);
	const [filteredPhases, setFilteredPhases] = useState([]);
	const cancelTokenSourceRef = useRef();

	const getPhases = useCallback(() => {
		if (!projectId || !cancelTokenSourceRef?.current?.token) {
			return;
		}
		if (typeof request === "function") {
			setIsLoading(true);
			request()
				.then((data) => {
					setPhases(data.contents || []);
				})
				.catch((err) => console.error(err))
				.finally(() => setIsLoading(false));
		} else {
			setIsLoading(true);
			CoveragePhaseService.getAllByProject({ projectId }, cancelTokenSourceRef.current.token)
				.then((data) => {
					setPhases(data?.contents || data?.content || data || []);
				})
				.catch((err) => console.error(err))
				.finally(() => setIsLoading(false));
		}
	}, [projectId, request]);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	useEffect(() => {
		getPhases();
	}, [getPhases]);

	useEffect(() => {
		if (search) {
			setFilteredPhases(phases.filter((phase) => phase.name.toLowerCase().includes(search.toLowerCase())));
		} else {
			setFilteredPhases(phases);
		}
	}, [search, phases]);

	const handleCheckPhase = (phase, isChecked) => {
		if (readOnly) {
			return;
		}
		const tempCheckedPhases =
			isNonEmptyObject(value) && Object.keys(value)?.some((v) => v !== "is" && v !== "matchAll")
				? { ...value }
				: { PHASE: [], is: value?.is || true, matchAll: value?.matchAll };

		if (isChecked) {
			tempCheckedPhases.PHASE = tempCheckedPhases.PHASE.filter((checkedPhase) => checkedPhase !== phase.id);
		} else {
			tempCheckedPhases.PHASE = [...tempCheckedPhases.PHASE, phase.id];
			tempCheckedPhases.PHASE = tempCheckedPhases.PHASE.filter(
				(val, index) => tempCheckedPhases.PHASE.indexOf(val) === index
			);
		}
		onChange(tempCheckedPhases);
	};

	const handleIsChecked = (phaseId) => value?.PHASE?.includes(phaseId) || false;

	return (
		<div>
			{(isLoading && (
				<div className={styles.loaderContainer}>
					<CircularLoader />
				</div>
			)) || (
				<div className={styles.phases__container}>
					{(isNonEmptyArray(filteredPhases) &&
						filteredPhases.map((phase) => {
							const isChecked = handleIsChecked(phase.id);
							return (
								<div key={phase.id} className={styles.phaseRow}>
									<Checkbox
										checked={isChecked}
										disabled={readOnly}
										size="small"
										onChange={() => handleCheckPhase(phase, isChecked)}
									/>
									<span className={styles.phaseName}>{phase.name}</span>
								</div>
							);
						})) || (
						<div className={styles.emptyState}>
							<IconComponent
								className={styles.emptyIcon}
								color="var(--blue-500)"
								icon={icon.faInfoCircle}
								size="lg"
							/>
							{translate("common:filters.project-phase-filter.no-phases")}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default ProjectPhaseFilter;
